import { Dialog, makeStyles } from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import CommonUtil from "../../Util/CommonUtils";
import { purchasedProducts } from "../../slices/ProductSlice/ProductSlice";
import { ThemeContext } from "../../theme/ThemeContext";

const useStyles = makeStyles((theme) => ({
  dialogPaper: (props) => ({
    borderRadius: "20px",
    minWidth: "900px",
    marginLeft: "180px",
    backgroundColor: props.darkMode ? "#0E2A41" : "#FFFFFF",
  }),
}));

export default function PlanDetailsDialog(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { darkMode } = useContext(ThemeContext);
  const classes = useStyles({ darkMode });
  const planName = props.planName;
  const [hardwareDevices, setHardwareDevices] = useState([]);
  const [accessoryDevices, setAccessoryDevices] = useState([]);
  const [purchasedProductsList, setPurchasedProductsList] = useState([]);
  const price = props.price;
  const companySettings = JSON.parse(localStorage.getItem("companySettings"));
  const { onSubmit, children, onClose, ...other } = props;

  const [dateFormat, setDateFormat] = useState(companySettings.dateFormat);

  const handleClose = () => {
    props.onClose(false);
  };

  useEffect(() => {
    loadPurchasedProducts();
  }, []);

  const keyStyle = {
    color: darkMode ? "#fff" : "rgba(71, 74, 78, 0.6)",
    fontFamily: "font_medium",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const valueStyle = {
    color: darkMode ? "#fff" : "rgba(71, 74, 78, 1)",
    fontFamily: "font_regular",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const loadPurchasedProducts = async () => {
    try {
      const res = await dispatch(
        purchasedProducts(localStorage.getItem("companyId"))
      ).unwrap();

      if (res.code === "MSGS4000") {
        const mergedData = res.data.reduce((acc, item) => {
          const existingItem = acc.find(
            (p) => p.stripeProductId === item.stripeProductId
          );

          if (existingItem) {
            existingItem.quantity += item.quantity;
          } else {
            acc.push({ ...item });
          }

          return acc;
        }, []);
        setHardwareDevices(mergedData);
        setPurchasedProductsList(mergedData);
        return mergedData;
      } else {
        console.error("Unexpected code:", res.code);
        return [];
      }
    } catch (error) {
      console.error("Error loading purchased products:", error);
      return [];
    }
  };

  return (
    <>
      <Dialog
        classes={{ paper: classes.dialogPaper }}
        {...props}
        open={props.open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
        }}
        disableBackdropClick
        disableEscapeKeyDown
        BackdropProps={{
          style: {
            backdropFilter: "blur(5px)",
          },
        }}
      >
        <DialogTitle
          variant="h4"
          sx={{
            color: darkMode ? "#fff" : "rgba(71, 74, 78, 1)",
            fontSize: "22px",
            fontFamily: "font_semiBold",
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 15,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          {props.Header}
        </DialogTitle>
        <Divider
          variant="middle"
          style={{
            background: darkMode
              ? "transparent linear-gradient(90deg, rgba(190, 231, 255, 0.05) 0%, rgba(34, 107, 171, 0.5) 47%, rgba(190, 231, 255, 0.05) 100%) 0% 0% no-repeat padding-box"
              : "transparent linear-gradient(90deg, rgba(190, 231, 255, 0.05) 0%, rgba(34, 107, 171, 0.5) 47%, rgba(190, 231, 255, 0.05) 100%) 0% 0% no-repeat padding-box",
            height: "1px",
            opacity: 0.5,
          }}
        />

        <DialogContent>
          <Grid
            container
            spacing={2}
            style={{
              justifyContent: "center",
              alignContent: "center",
              alignItems: "center",
              color: darkMode ? "#fff" : "#3D4977",
            }}
          >
            <Grid
              item
              sm={6}
              style={{
                borderRight: "2px solid transparent",
                borderImage:
                  "linear-gradient(180deg, #BEE7FF0D 0%, #226BAB80 47%, #BEE7FF0D 100%) 1",
                borderImageSlice: 1,
                minHeight: "160px",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <Grid
                container
                style={{
                  color: darkMode ? "#D5D5D5" : "rgba(71, 74, 78, 0.8)",
                  fontSize: "18px",
                  fontFamily: "font_semiBold",
                }}
              >
                {t("DEALERPLANDETAILS")}
              </Grid>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={3}
                  lg={3}
                  xl={3}
                  style={keyStyle}
                >
                  {t("SUBSCRIPTIONPLANNAME")}
                </Grid>
                <Grid xs={12} sm={12} md={0.3} lg={0.3} xl={0.3}>
                  :
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={8.7}
                  lg={8.7}
                  xl={8.7}
                  style={valueStyle}
                >
                  {props.planDetails.subscriptionPurchaseId.subscriptionName}
                </Grid>
              </Grid>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={3}
                  lg={3}
                  xl={3}
                  style={keyStyle}
                >
                  {t("START_DATE")}
                </Grid>
                <Grid xs={12} sm={12} md={0.3} lg={0.3} xl={0.3}>
                  :
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={8.7}
                  lg={8.7}
                  xl={8.7}
                  style={valueStyle}
                >
                  {dateFormat === "dd-MM-yyyy"
                    ? moment(
                        CommonUtil.getLocalDateTimeInYearFormat(
                          props.planDetails.subscriptionPurchaseId.startDate
                        )
                      ).format("DD-MM-YYYY HH:mm")
                    : dateFormat === "MM-dd-yyyy"
                    ? moment(
                        CommonUtil.getLocalDateTimeInYearFormat(
                          props.planDetails.subscriptionPurchaseId.startDate
                        )
                      ).format("MM-DD-YYYY HH:mm")
                    : dateFormat === "yyyy-MM-dd"
                    ? moment(
                        CommonUtil.getLocalDateTimeInYearFormat(
                          props.planDetails.subscriptionPurchaseId.startDate
                        )
                      ).format("YYYY-MM-DD HH:mm")
                    : ""}
                </Grid>
              </Grid>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={3}
                  lg={3}
                  xl={3}
                  style={keyStyle}
                >
                  {t("END_DATE")}
                </Grid>
                <Grid xs={12} sm={12} md={0.3} lg={0.3} xl={0.3}>
                  :
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={8.7}
                  lg={8.7}
                  xl={8.7}
                  style={valueStyle}
                >
                  {dateFormat === "dd-MM-yyyy"
                    ? moment(
                        CommonUtil.getLocalDateTimeInYearFormat(
                          props.planDetails.subscriptionPurchaseId.endDate
                        )
                      ).format("DD-MM-YYYY HH:mm")
                    : dateFormat === "MM-dd-yyyy"
                    ? moment(
                        CommonUtil.getLocalDateTimeInYearFormat(
                          props.planDetails.subscriptionPurchaseId.endDate
                        )
                      ).format("MM-DD-YYYY HH:mm")
                    : dateFormat === "yyyy-MM-dd"
                    ? moment(
                        CommonUtil.getLocalDateTimeInYearFormat(
                          props.planDetails.subscriptionPurchaseId.endDate
                        )
                      ).format("YYYY-MM-DD HH:mm")
                    : ""}
                </Grid>
              </Grid>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={3}
                  lg={3}
                  xl={3}
                  style={keyStyle}
                >
                  {t("RENEWAL_DATE")}
                </Grid>
                <Grid xs={12} sm={12} md={0.3} lg={0.3} xl={0.3}>
                  :
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={8.7}
                  lg={8.7}
                  xl={8.7}
                  style={valueStyle}
                ></Grid>
              </Grid>
            </Grid>
            <Grid
              item
              sm={6}
              style={{
                minHeight: "190px",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <Grid
                container
                style={{
                  color: darkMode ? "#D5D5D5" : "rgba(71, 74, 78, 0.8)",
                  fontSize: "18px",
                  fontFamily: "font_semiBold",
                }}
              >
                {t("DEALERDETAILS")}
              </Grid>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={3}
                  lg={3}
                  xl={3}
                  style={keyStyle}
                >
                  {t("CUSTDATABLE22")}
                </Grid>
                <Grid xs={12} sm={12} md={0.3} lg={0.3} xl={0.3}>
                  :
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={8.7}
                  lg={8.7}
                  xl={8.7}
                  style={valueStyle}
                >
                  <Tooltip title={props.parentDetails.name}>
                    {props.parentDetails.name}
                  </Tooltip>
                </Grid>
              </Grid>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={3}
                  lg={3}
                  xl={3}
                  style={keyStyle}
                >
                  {t("DEALEREMAIL")}
                </Grid>
                <Grid xs={12} sm={12} md={0.3} lg={0.3} xl={0.3}>
                  :
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={8.7}
                  lg={8.7}
                  xl={8.7}
                  style={valueStyle}
                >
                  <Tooltip title={props.parentDetails.email}>
                    {props.parentDetails.email}
                  </Tooltip>
                </Grid>
              </Grid>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={3}
                  lg={3}
                  xl={3}
                  style={keyStyle}
                >
                  {t("COMMON063")}
                </Grid>
                <Grid xs={12} sm={12} md={0.3} lg={0.3} xl={0.3}>
                  :
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={8.7}
                  lg={8.7}
                  xl={8.7}
                  style={valueStyle}
                >
                  {props.parentDetails.phone || " "}
                </Grid>
              </Grid>
            </Grid>
            {/* <Grid
              item
              sm={12}
              style={{
                display: "flex",
                justifyContent: "flex-start",
                color: darkMode ? "#D5D5D5" : "rgba(71, 74, 78, 0.8)",
                fontSize: "18px",
                fontFamily: "font_semiBold",
                paddingTop: "8px",
              }}
            >
              {t("PRODUCT_DETAILS")}
            </Grid> */}
          </Grid>
          {/* <Grid
            container
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "20px",
              marginTop: "15px",
            }}
          >
            {hardwareDevices.length > 0 && (
              <>
                {hardwareDevices.map((product, index) => (
                  <Box
                    key={`hardware-${index}`}
                    position="relative"
                    style={{
                      height: "100px",
                      backgroundColor: "#F9F9F9",
                      display: "flex",
                      alignItems: "center",
                      width: "250px",
                      padding: "10px",
                      borderRadius: "8px",
                    }}
                  >
                    <Box
                      style={{
                        height: "62px",
                        width: "62px",
                        backgroundColor: "#D8D8D8",
                        borderRadius: "6px",
                        marginRight: "15px",
                        overflow: "hidden",
                      }}
                    >
                      {product.product.productImagePresignedUrl ? (
                        <img
                          src={product.product.productImagePresignedUrl}
                          alt="Product"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Box
                          style={{
                            width: "100%",
                            height: "100%",
                            backgroundColor: "#E0E0E0",
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      style={{
                        color: "#474A4E",
                        fontSize: "18px",
                        fontFamily: "font_bold",
                        lineHeight: "1.2",
                      }}
                    >
                      {product.product.productName.length > 10 ? (
                        <Tooltip
                          title={product.product.productName}
                          placement="bottom-start"
                        >
                          <span>
                            {product.product.productName.substring(0, 10)}...
                          </span>
                        </Tooltip>
                      ) : (
                        <span>{product.product.productName}</span>
                      )}
                      {product.product.productModel && (
                        <>
                          <span> + </span>
                          {product.product.productModel.length > 10 ? (
                            <Tooltip
                              title={product.product.productModel}
                              placement="bottom-start"
                            >
                              <span>
                                {product.product.productModel.substring(0, 10)}
                                ...
                              </span>
                            </Tooltip>
                          ) : (
                            <span>{product.product.productModel}</span>
                          )}
                        </>
                      )}
                    </Typography>

                    <Typography
                      component="span"
                      style={{
                        fontSize: "18px",
                        color: "#226BAB",
                      }}
                    >
                      {" ("}
                      {product.quantity}
                      {")"}
                    </Typography>
                  </Box>
                ))}
              </>
            )}
          </Grid> */}
        </DialogContent>
      </Dialog>
    </>
  );
}
